import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModelComboConfig } from '../../types/api';
import { APP_BASE_PATH, USER_MODULE } from '../../config';
import UserMenu from '../auth/UserMenu';
import { ModelComboPanel } from './ModelComboPanel';
import { useCloseOnOutsideClick } from '../../hooks/useCloseOnOutsideClick';

interface TopBarProps {
  selectedDomain: string;
  availableDomains: string[];
  datasetTotals: Record<string, number | undefined>;
  selectedModelCombo: string;
  availableModelCombos: string[];
  modelCombos: Record<string, ModelComboConfig>;
  domainDropdownOpen: boolean;
  modelDropdownOpen: boolean;
  helpDropdownOpen: boolean;
  showDomainTooltip: boolean;
  onToggleDomainDropdown: () => void;
  onToggleModelDropdown: () => void;
  onDomainMouseEnter: () => void;
  onDomainMouseLeave: () => void;
  onCloseDomainDropdown: () => void;
  onCloseModelDropdown: () => void;
  onSelectDomain: (domainName: string) => void;
  onSelectModelCombo: (comboName: string) => void;
  onToggleHelpDropdown: () => void;
  onCloseHelpDropdown: () => void;
  onAboutClick: () => void;
  onTechClick: () => void;
  onDataClick: () => void;
  onDocsClick: () => void;
  onAdminClick?: () => void;
  onLoadResearch?: (id: string) => void;
  navTabs?: React.ReactNode;
}

export const TopBar = ({
  selectedDomain,
  availableDomains,
  datasetTotals,
  selectedModelCombo,
  availableModelCombos,
  modelCombos,
  domainDropdownOpen,
  modelDropdownOpen,
  helpDropdownOpen,
  showDomainTooltip,
  onToggleDomainDropdown,
  onToggleModelDropdown,
  onDomainMouseEnter,
  onDomainMouseLeave,
  onCloseDomainDropdown,
  onCloseModelDropdown,
  onSelectDomain,
  onSelectModelCombo,
  onToggleHelpDropdown,
  onCloseHelpDropdown,
  onAboutClick,
  onTechClick,
  onDataClick,
  onDocsClick,
  onAdminClick,
  onLoadResearch,
  navTabs,
}: TopBarProps) => {
  const [hoveredModelCombo, setHoveredModelCombo] = useState<string | null>(null);
  const domainRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  useCloseOnOutsideClick(domainRef, domainDropdownOpen, onCloseDomainDropdown);
  useCloseOnOutsideClick(modelRef, modelDropdownOpen, onCloseModelDropdown);
  useCloseOnOutsideClick(helpRef, helpDropdownOpen, onCloseHelpDropdown);

  useEffect(() => {
    if (!modelDropdownOpen) {
      setHoveredModelCombo(null);
    }
  }, [modelDropdownOpen]);

  const activeModelCombo = hoveredModelCombo || selectedModelCombo;
  const fallbackComboName = availableModelCombos[0];
  const activeComboConfig =
    modelCombos[activeModelCombo] || modelCombos[fallbackComboName];
  const displayComboName =
    modelCombos[activeModelCombo] ? activeModelCombo : fallbackComboName;

  const numberFormatter = useMemo(() => new Intl.NumberFormat('en-US'), []);

  const modelTriggerMinWidth = useMemo(() => {
    if (!availableModelCombos.length) {
      return undefined;
    }
    const longestCombo = availableModelCombos.reduce(
      (longest, current) => (current.length > longest.length ? current : longest),
      ''
    );
    const charCount = 'Models'.length + longestCombo.length + 1;
    return `calc(${charCount}ch + 3.5rem)`;
  }, [availableModelCombos]);

  const getDomainLabelText = (domainName: string) => {
    const total = datasetTotals[domainName];
    if (total === undefined || Number.isNaN(total)) {
      return domainName;
    }
    return `${domainName} (${numberFormatter.format(total)})`;
  };

  const renderDomainLabel = (domainName: string) => {
    const total = datasetTotals[domainName];
    if (total === undefined || Number.isNaN(total)) {
      return domainName;
    }
    return (
      <>
        {domainName} <span className="dataset-total">({numberFormatter.format(total)})</span>
      </>
    );
  };

  const datasetTriggerMinWidth = useMemo(() => {
    if (!availableDomains.length) {
      return undefined;
    }
    const longestLabel = availableDomains.reduce((longest, current) => {
      const label = getDomainLabelText(current);
      return label.length > longest.length ? label : longest;
    }, '');
    const charCount = 'Dataset'.length + longestLabel.length;
    return `calc(${charCount}ch + 1.5rem)`;
  }, [availableDomains, getDomainLabelText]);

  // Clicking the logo/title returns the user to the home page.
  const homeHref = `${APP_BASE_PATH}/`;
  const handleBrandClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Let modified clicks (new tab/window) and non-primary buttons behave
    // natively so the underlying link still works as expected.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    window.history.pushState(null, '', homeHref);
    // Reuse the app's existing popstate routing to reset to the home view
    // without a full page reload.
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <header className="top-bar">
      <div className="top-bar-content">
        <div className="top-bar-left">
          <a
            className="top-bar-brand"
            href={homeHref}
            onClick={handleBrandClick}
            aria-label="Evidence Lab — go to home page"
          >
            <img src="/logo.png" alt="Evidence Lab Logo" className="app-logo" />
            <h1 className="app-title">Evidence Lab</h1>
          </a>
          <div className="top-bar-controls">
            <div className="dropdown-container" ref={domainRef}>
            <button
              className="dropdown-trigger dropdown-trigger-domain"
              onClick={onToggleDomainDropdown}
              onMouseEnter={onDomainMouseEnter}
              onMouseLeave={onDomainMouseLeave}
              style={datasetTriggerMinWidth ? { minWidth: datasetTriggerMinWidth } : undefined}
            >
              <span className="dropdown-label">Dataset</span>
              <span className="dropdown-value">{renderDomainLabel(selectedDomain)}</span>
              <span className="dropdown-arrow">▾</span>
            </button>
            {showDomainTooltip && !domainDropdownOpen && (
              <div className="tooltip-text">
                Select the data domain you want to experiment with
              </div>
            )}
            {domainDropdownOpen && (
              <div className="dropdown-menu dropdown-menu-domain">
                {availableDomains.map((domainName) => (
                  <button
                    key={domainName}
                    className="dropdown-item"
                    onClick={() => onSelectDomain(domainName)}
                  >
                    {renderDomainLabel(domainName)}
                  </button>
                ))}
              </div>
            )}
          </div>

            <div className="dropdown-container" ref={modelRef}>
            <button
              className="dropdown-trigger"
              onClick={onToggleModelDropdown}
              style={modelTriggerMinWidth ? { minWidth: modelTriggerMinWidth } : undefined}
            >
              <span className="dropdown-label">Models</span>
              <span className="dropdown-value">{selectedModelCombo}</span>
              <span className="dropdown-arrow">▾</span>
            </button>
            {modelDropdownOpen && (
              <div
                className="dropdown-menu dropdown-menu-models"
              >
                <div className="dropdown-menu-list">
                  {availableModelCombos.map((comboName) => (
                    <button
                      key={comboName}
                      className="dropdown-item"
                      onClick={() => onSelectModelCombo(comboName)}
                      onMouseEnter={() => setHoveredModelCombo(comboName)}
                    >
                      {comboName}
                    </button>
                  ))}
                </div>
                {activeComboConfig && (
                  <ModelComboPanel config={activeComboConfig} displayName={displayComboName} />
                )}
              </div>
            )}
            </div>
          </div>
        </div>

        <div className="top-bar-right">
          <div className="dropdown-container" ref={helpRef}>
            <button
              className="dropdown-trigger dropdown-trigger-help"
              onClick={onToggleHelpDropdown}
            >
              <span className="dropdown-value">Info</span>
              <span className="dropdown-arrow">▾</span>
            </button>
            {helpDropdownOpen && (
              <div className="dropdown-menu dropdown-menu-help">
                <button className="dropdown-item" onClick={onAboutClick}>
                  About
                </button>
                <button className="dropdown-item" onClick={onTechClick}>
                  Tech
                </button>
                <button className="dropdown-item" onClick={onDataClick}>
                  Data
                </button>
                <button className="dropdown-item" onClick={onDocsClick}>
                  Docs
                </button>
              </div>
            )}
          </div>
          {USER_MODULE && <UserMenu onAdminClick={onAdminClick} onLoadResearch={onLoadResearch} />}
        </div>
      </div>
      {navTabs}
    </header>
  );
};
